import { useEffect, useRef, useState } from 'react';
import { IonSpinner } from '@ionic/react';
import { Trans, useTranslation } from 'react-i18next';
import { UserAttribution } from '../ui/UserAttribution';
import { VerificationCode } from './VerificationCode';
import { OrderStatusBreadcrumb } from './OrderStatusBreadcrumb';
import { ReviewModal } from '../activity/ReviewModal';
import { RejectOrderModal } from './RejectOrderModal';
import { Modal } from '../ui/Modal';
import { usePatchOrderStatus, useListerConfirmOrder, useNudgeOrder } from '../../hooks/useOrders';
import { useOrderVisibility } from '../../hooks/useOrderVisibility';
import type { OrderStatus } from '../../types/order';
import type { ListingOrderSummary } from '../../services/orderService';
import { fireConfetti } from '../../utils/confetti';
import { formatPriceCents } from '../../utils/currency';
import { formatRelativeTime } from '../../utils/relativeTime';
import { formatPickupMoment, formatTimeWindow, presentWindow } from '../../utils/timeWindow';
import { OpenInMapsButton } from './OpenInMapsButton';
import { zonedWallClockToInstant } from '../../utils/timezone';

/**
 * Map current status → next lister action.
 *
 * `labelKey` references orders.json — the label IS rendered via t() so the
 * lister sees the action button in their UI language. The wire-facing
 * `status` value is unchanged (BE expects 'accepted' / 'ready_to_pickup'
 * regardless of caller language).
 */
const LISTER_NEXT_STATUS: Partial<Record<OrderStatus, { status: OrderStatus; labelKey: string }>> = {
  received: { status: 'accepted', labelKey: 'card.action.accept' },
  accepted: { status: 'ready_to_pickup', labelKey: 'card.action.readyForPickup' },
};

const LISTER_CANCELABLE: OrderStatus[] = ['paid', 'received', 'accepted', 'ready_to_pickup'];
const LISTER_POST_ACCEPT: OrderStatus[] = ['accepted', 'ready_to_pickup'];

interface OrderCardProps {
  order: ListingOrderSummary;
  isLister?: boolean;
  activityId?: string;
  onReviewed?: () => void;
  /** Buttons rendered in the card's reserved top-right action zone. Header content shifts to make room when provided. */
  topRightActions?: React.ReactNode;
  /** Optional slot rendered inside the card above the order header (e.g. a DishInfoCard for the orderer feed). */
  listingHeader?: React.ReactNode;
  /** Orderer-side verification UI (interactive bubbles + error). Replaces the lister's readonly code box at handoff stages. */
  verificationSlot?: React.ReactNode;
  /** DOM id for the card's root, so a parent can anchor/deep-link to it. */
  anchorId?: string;
  /** When true (a notification deep-linked to this order), scroll the card into view on mount and flash a highlight ring. */
  highlight?: boolean;
  /**
   * IANA zone of the listing this order is against, when the caller knows it.
   * `intendedPickupTime` is a naive wall-clock in that zone, so without it the
   * pickup reads in the viewer's zone instead — wrong for a cross-timezone
   * order, and wrong about which DAY it falls on. Optional because the
   * lister-side summary endpoint doesn't carry the listing; callers that have
   * it should pass it.
   */
  pickupTimezone?: string;
  /**
   * Lister, accepted drop-off: opens the listing page where the drop-off run is
   * started. Passed by surfaces that sit away from that page (the notification
   * center), so an accepted drop-off card there still has somewhere to go. The
   * listing page itself omits it — the run panel is already right above.
   */
  onOpenDropoffRun?: () => void;
  /** Other drop-off orders on the listing still awaiting accept/decline. When
   *  above zero the run is locked, so the button says to review them instead. */
  dropoffRunPendingCount?: number;
}

export const OrderCard: React.FC<OrderCardProps> = ({ order, isLister: isListerProp, activityId, onReviewed, topRightActions, listingHeader, verificationSlot, anchorId, highlight, pickupTimezone, onOpenDropoffRun, dropoffRunPendingCount = 0 }) => {
  const { t } = useTranslation('orders');
  // Pickup used to render as a bare clock time here, so an order placed for
  // Saturday looked identical to one for tonight. Name the day too — same
  // Today / Tomorrow / Saturday vocabulary as the feed card and the picker.
  const pickupMoment = formatPickupMoment(order.intendedPickupTime, pickupTimezone);
  const statusMutation = usePatchOrderStatus();
  const ackMutation = usePatchOrderStatus();
  const listerConfirmMutation = useListerConfirmOrder();
  const nudgeMutation = useNudgeOrder();
  const [showReview, setShowReview] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [showNoShow, setShowNoShow] = useState(false);
  const receivedRef = useRef(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const status = order.status as OrderStatus;
  const prevStatusRef = useRef(status);
  const isLister = isListerProp ?? !!order.orderedFor;
  // Drop-off reverses who travels: the chef drives one run for the whole
  // listing, so there's no per-order "ready" tap, no "come get it" nudge, and
  // the address that matters is the eater's, not the chef's.
  const isDropoff = order.fulfillmentMethod === 'dropoff';
  // A drop-off arrives somewhere inside the chef's window rather than at a
  // picked time, so state the window. Falls back to its start for a payload
  // that doesn't carry the window.
  const handoffHeading = isDropoff
    ? presentWindow(order.dropoffWindow)
      ? `${t('card.dropoffHeading')} ${formatTimeWindow(order.dropoffWindow!)}`
      : pickupMoment
        ? `${t('card.dropoffHeading')} ${pickupMoment.full}`
        : null
    : pickupMoment
      ? `${t('card.pickupHeading')} ${pickupMoment.full}`
      : null;

  // Dismiss this order's toast once its card is actually seen on screen, and
  // mark it visible so an incoming toast isn't made sticky over it.
  useOrderVisibility(order.orderId, rootRef);

  // Notification deep-link: when this card is the targeted order, scroll it
  // into view and flash a highlight ring that fades after a beat. rAF defers
  // the scroll one frame so it runs after layout settles on first paint.
  const [flashHighlight, setFlashHighlight] = useState(false);
  useEffect(() => {
    if (!highlight) return;
    const raf = requestAnimationFrame(() => {
      rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    setFlashHighlight(true);
    const timer = setTimeout(() => setFlashHighlight(false), 2500);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [highlight]);

  useEffect(() => {
    if (prevStatusRef.current !== 'completed' && status === 'completed') {
      fireConfetti();
    }
    prevStatusRef.current = status;
  }, [status]);
  const counterparty = isLister ? order.orderedFor : order.lister;
  const counterpartyName = counterparty?.businessName ?? counterparty?.displayName ?? '';

  // The lister is made whole on GROSS (price * qty) only — `totalPaid` is the
  // eater's charge, with platform fees and tax stacked on top that never reach
  // the lister. Showing them the eater's total reads as a promise of money they
  // won't receive, so every lister-facing card shows gross instead.
  // `subtotal` comes straight from the API; the pricePerUnit fallback keeps the
  // number right against a backend that predates that field.
  const grossCents =
    order.subtotal ??
    (order.pricePerUnit != null ? order.pricePerUnit * order.quantity : undefined);
  const displayAmount = isLister ? grossCents : order.totalPaid;
  // Nothing beats a wrong number: with neither gross source available we drop
  // the line rather than fall back to the eater's total (formatPriceCents would
  // also render a bare undefined as "Free").
  const showAmount = displayAmount != null;

  // Auto-acknowledge: when the lister sees a paid order, mark it received.
  useEffect(() => {
    if (isLister && status === 'paid' && !receivedRef.current) {
      receivedRef.current = true;
      ackMutation.mutate({ id: order.orderId, status: 'received' });
    }
  }, [isLister, status, order.orderId]);
  // Accepted drop-off orders go out together via the listing's drop-off run
  // (DropoffRunPanel), never one at a time — the backend refuses it too.
  const nextAction =
    isDropoff && status === 'accepted' ? null : (LISTER_NEXT_STATUS[status] ?? null);

  const canCancel = LISTER_CANCELABLE.includes(status);
  const showReviewAction = order.reviewEligible && !order.hasReviewed;
  // No-show is lister-only, only from ready_to_pickup, and only after the
  // listing's pickup window has closed. Backend re-checks all three; this
  // just controls whether the button is rendered at all.
  //
  // A drop-off order is timed off its own drop-off window, matching the
  // backend: the listing window spans both methods, so on a listing whose
  // pickup runs later it would hold the button back for no reason.
  const dropoffEnd = isDropoff ? presentWindow(order.dropoffWindow) : null;
  const windowEndMs = dropoffEnd
    ? zonedWallClockToInstant(dropoffEnd.end, dropoffEnd.timezone).getTime()
    : order.listingTimeWindowEnd
      ? new Date(order.listingTimeWindowEnd).getTime()
      : null;
  const canMarkNoShow =
    isLister &&
    status === 'ready_to_pickup' &&
    windowEndMs != null &&
    windowEndMs < Date.now();
  // Reopen-after-no-show: lister can flip a no_show back to ready_to_pickup
  // (for the late-arrival case) within 1 hour of the listing's pickup window
  // close. Stripe capture stays in place; the normal verification flow
  // finishes the order from there.
  const canReopenNoShow =
    isLister &&
    status === 'no_show' &&
    windowEndMs != null &&
    Date.now() < windowEndMs + 60 * 60 * 1000;

  const handleAdvance = () => {
    if (!nextAction) return;
    statusMutation.mutate({ id: order.orderId, status: nextAction.status });
  };

  const handleReject = (reason: string) => {
    statusMutation.mutate(
      { id: order.orderId, status: 'canceled', rejectionReason: reason },
      { onSuccess: () => setShowReject(false) },
    );
  };

  const handleNoShow = () => {
    statusMutation.mutate(
      { id: order.orderId, status: 'no_show' },
      { onSuccess: () => setShowNoShow(false) },
    );
  };

  const handleReopenNoShow = () => {
    statusMutation.mutate({ id: order.orderId, status: 'ready_to_pickup' });
  };

  // Lister's half of the completion handshake. Completes the order if the
  // orderer already confirmed; otherwise records the confirm and pings the
  // orderer once. Only rendered at handoff stages for the lister.
  const showListerConfirm = isLister && status === 'ready_to_pickup';
  const listerConfirmBusy = listerConfirmMutation.isPending;
  // The three handshake states: orderer already confirmed → this press completes;
  // lister already confirmed (waiting) → disabled; neither → confirm + remind.
  const ordererHasConfirmed = !!order.ordererConfirmed;
  const listerIsWaiting = !!order.listerConfirmed && !ordererHasConfirmed;
  const handleListerConfirm = () => {
    if (listerConfirmBusy || listerIsWaiting) return;
    listerConfirmMutation.mutate({ id: order.orderId });
  };

  // The "come get it" prod, a separate action from the confirm above with its own
  // one-shot budget — a lister who nudges early must still be able to press
  // Handed Off when the eater shows up. It drops away as soon as the eater is
  // en route, has confirmed, or the lister has handed off — at each of those
  // points telling them to come get it is stale, and the backend rejects the
  // nudge outright once handed off (nudgeHandler).
  const showNudge =
    isLister &&
    !isDropoff &&
    status === 'ready_to_pickup' &&
    !order.onMyWay &&
    !order.listerConfirmed &&
    !ordererHasConfirmed;
  const nudgeBusy = nudgeMutation.isPending;
  const nudgeSpent = !!order.listerNudged;
  const handleNudge = () => {
    if (nudgeBusy || nudgeSpent) return;
    nudgeMutation.mutate({ id: order.orderId });
  };

  const isBusy = statusMutation.isPending;

  return (
    <div
      ref={rootRef}
      id={anchorId}
      className={`relative rounded-xl border bg-card text-card-foreground p-4 space-y-3 transition-shadow duration-500 ${
        flashHighlight ? 'border-brand-primary ring-2 ring-brand-primary' : 'border-border'
      }`}
    >
      {/* z-10 is load-bearing, not decorative: `listingHeader` (DishInfoCard)
          wraps its photo in a `relative` container, and two positioned elements
          that both resolve to `z-index: auto` paint in DOM order — so without
          this the image, which comes later, covered the top-right corner of
          these actions. */}
      {topRightActions && (
        <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
          {topRightActions}
        </div>
      )}
      {/* pt-7 opens a band above the header art for the button to sit in, so
          the photo starts below it instead of running underneath. 28px on top
          of the card's own 16px padding clears the button (8px inset + 28px
          tall) with a little breathing room. */}
      {listingHeader && (
        <div className={topRightActions ? 'pt-7' : undefined}>{listingHeader}</div>
      )}
      {/* Header: when DishInfoCard slot is present, attribution lives there;
          pickup time moves to the left and the right shows total/qty/order#. */}
      {listingHeader ? (
        // flex-wrap + ml-auto: the right-hand metrics block sits beside the
        // left info at normal text but drops to its own (right-aligned) line at
        // large text instead of crunching. whitespace-nowrap keeps each metric
        // line intact rather than breaking mid-phrase.
        <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
          <div className="min-w-0">
            {handoffHeading && (
              <p className="text-sm font-medium">{handoffHeading}</p>
            )}
            {showAmount && (
              <p className="text-sm font-semibold">
                {formatPriceCents(displayAmount)}
              </p>
            )}
          </div>
          <div className={`ml-auto text-right ${topRightActions ? 'pr-7' : ''}`}>
            <p className="whitespace-nowrap text-xs text-muted-foreground">{t('card.qty', { qty: order.quantity })}</p>
            <p className="whitespace-nowrap text-xs text-muted-foreground">{t('card.orderedAgo', { relative: formatRelativeTime(order.createdAt) })}</p>
          </div>
        </div>
      ) : (
        // Same wrap behavior; the attribution keeps a rem-based min-width so it
        // stays readable and the metrics block wraps below it at large text.
        <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
          <div className="min-w-[11rem] flex-1">
            <UserAttribution
              summary={counterparty}
              size="md"
              nameClassName="text-sm font-medium"
              interactive
            />
            <p className="text-xs text-muted-foreground">{order.orderNumber}</p>
            <p className="text-xs text-muted-foreground">{t('card.orderedAgo', { relative: formatRelativeTime(order.createdAt) })}</p>
          </div>
          <div className={`ml-auto text-right ${topRightActions ? 'pr-7' : ''}`}>
            {showAmount && (
              <p className="whitespace-nowrap text-sm font-semibold">
                {formatPriceCents(displayAmount)}
              </p>
            )}
            <p className="whitespace-nowrap text-xs text-muted-foreground">
              {t('card.qty', { qty: order.quantity })} · {t(`card.fulfillment.${order.fulfillmentMethod}` as const, { defaultValue: order.fulfillmentMethod })}
            </p>
            {handoffHeading && (
              // No `whitespace-nowrap` here, unlike the metric lines above: with
              // the day in it this is a phrase, and at large text it would
              // overflow the right-aligned column rather than hold a line.
              <p className="text-xs text-muted-foreground">{handoffHeading}</p>
            )}
          </div>
        </div>
      )}

      {/* Status breadcrumb */}
      <OrderStatusBreadcrumb status={status} fulfillmentMethod={order.fulfillmentMethod} />

      {/* Drop-off, lister side: where to drive. The backend only sends the
          address once the chef has accepted, so before that this is the
          "you'll see it after accepting" placeholder. */}
      {isLister && isDropoff && status !== 'canceled' && status !== 'expired' && (
        <div className="rounded-lg border border-border p-3 text-sm">
          <p className="text-xs text-muted-foreground">{t('card.deliverTo')}</p>
          {order.orderAddress ? (
            <div className="space-y-2">
              <div>
                <p className="font-medium text-foreground">
                  {order.orderAddress.street1 || order.orderAddress.address}
                </p>
                {order.orderAddress.street2 && (
                  <p className="text-foreground">{order.orderAddress.street2}</p>
                )}
                <p className="text-muted-foreground">
                  {[order.orderAddress.city, order.orderAddress.stateProvince, order.orderAddress.postalCode]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              </div>
              <OpenInMapsButton
                destination={order.orderAddress}
                label={counterpartyName || undefined}
              />
            </div>
          ) : (
            <p className="text-muted-foreground">{t('card.deliverToAfterAccept')}</p>
          )}
        </div>
      )}

      {/* Accepted drop-off: nothing to tap per order. Point at the run. */}
      {isLister && isDropoff && status === 'accepted' && (
        <div className="space-y-2">
          <p className="text-center text-sm text-muted-foreground">
            {dropoffRunPendingCount > 0
              ? t('card.goesOutWithRunPending', { count: dropoffRunPendingCount })
              : t('card.goesOutWithRun')}
          </p>
          {onOpenDropoffRun && (
            <button
              type="button"
              onClick={onOpenDropoffRun}
              className="w-full rounded-lg btn-outline border-brand-primary py-2 text-sm font-semibold text-brand-primary active:opacity-80"
            >
              {dropoffRunPendingCount > 0 ? t('card.reviewDropoffOrders') : t('card.openDropoffRun')}
            </button>
          )}
        </div>
      )}

      {/* Buyer-en-route indicator (lister side): once the orderer taps "On My
          Way" on a pickup order, the lister sees a persistent heads-up so a
          missed push doesn't leave them unaware the eater is coming. The
          read-only code surfaces alongside it (its code is gated on the
          same on-my-way signal). Drops once the orderer confirms the handoff —
          at that point they've arrived, so "on their way" is stale. */}
      {isLister && status === 'ready_to_pickup' && order.onMyWay && !order.ordererConfirmed && (
        <div className="rounded-lg bg-brand-primary/10 px-3 py-2 text-center text-sm font-medium text-brand-primary">
          {(() => {
            const eaterName = order.orderedFor?.businessName || order.orderedFor?.displayName;
            return eaterName
              ? t('card.onMyWayBadge', { name: eaterName })
              : t('card.onMyWayBadgeNoName');
          })()}
        </div>
      )}

      {/* Verification — at handoff stages, lister sees the same text-over-
          VerificationCode layout the orderer sees in VerificationBubbles, so
          both sides feel parallel; the lister just has no confirm button here
          (their half is Handed Off, below). Orderer's interactive variant
          arrives via verificationSlot.

          The blurb tracks the handshake the same way VerificationBubbles does
          on the orderer's side: "will ask for this number" is only true before
          either half lands. Once the number has actually been run, future
          tense reads as wrong. Precedence matches the bubbles too. The
          orderer's confirm is strictly later than the lister's, so it wins. */}
      {status === 'ready_to_pickup' && (
        isLister
          ? order.verificationCode && (
              <div className="rounded-xl border border-border p-4 space-y-3">
                <p className="text-center text-xs text-muted-foreground">
                  {t('card.verificationHeader')}
                </p>
                <div className="space-y-3">
                  <p className="whitespace-pre-line text-sm font-medium">
                    {ordererHasConfirmed ? (
                      counterpartyName ? (
                        <Trans
                          i18nKey="orders:card.verificationConfirmed"
                          values={{ displayName: counterpartyName }}
                          components={{
                            name: <span className="font-semibold text-brand-primary" />,
                          }}
                        />
                      ) : (
                        t('card.verificationConfirmedNoName')
                      )
                    ) : listerIsWaiting ? (
                      counterpartyName ? (
                        <Trans
                          i18nKey="orders:card.verificationHandedOff"
                          values={{ displayName: counterpartyName }}
                          components={{
                            name: <span className="font-semibold text-brand-primary" />,
                          }}
                        />
                      ) : (
                        t('card.verificationHandedOffNoName')
                      )
                    ) : counterpartyName ? (
                      <Trans
                        i18nKey="orders:card.verificationCode"
                        values={{ displayName: counterpartyName }}
                        components={{
                          name: <span className="font-semibold text-brand-primary" />,
                        }}
                      />
                    ) : (
                      t('card.verificationCodeNoName')
                    )}
                  </p>
                  <VerificationCode code={order.verificationCode} confirmed={ordererHasConfirmed} />
                </div>
              </div>
            )
          : verificationSlot
      )}

      {/* Lister's handoff zone — two independent one-shot actions side by side.
          Remind is a pure "come get it" poke (pickup only); Handed Off is the
          lister's half of the completion handshake, which both parties must
          press before the order completes and captures. They're separate
          endpoints on purpose: nudging early must leave the confirm available.
          The prompt above them narrates whichever step is outstanding. */}
      {showListerConfirm && (
        <div className="space-y-2">
          <p className="text-center text-sm text-muted-foreground">
            {ordererHasConfirmed
              ? t('card.confirm.ordererConfirmed')
              : listerIsWaiting
                ? t('card.confirm.waitingOnOrderer')
                : isDropoff
                  ? t('card.confirm.dropoffHandoffPrompt')
                  : order.onMyWay
                  ? t('card.confirm.handoffPrompt')
                  : nudgeSpent
                    ? t('card.confirm.nudgeSentPrompt')
                    : t('card.confirm.remindPrompt')}
          </p>
          {/* Same wrap/equal-width rule as the action row below: side by side at
              normal text, stacked full-width rows at large text. */}
          <div className="flex flex-wrap gap-2">
            {showNudge && (
              <button
                type="button"
                onClick={handleNudge}
                disabled={nudgeBusy || nudgeSpent}
                className="flex min-w-[8rem] flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg btn-outline border-brand-primary px-3 py-2 text-sm font-semibold text-brand-primary active:opacity-80 disabled:opacity-50"
              >
                {nudgeBusy ? (
                  <IonSpinner name="crescent" className="h-4 w-4" />
                ) : nudgeSpent ? (
                  t('card.confirm.nudgeSentCta')
                ) : (
                  t('card.confirm.remindCta')
                )}
              </button>
            )}
            <button
              type="button"
              onClick={handleListerConfirm}
              disabled={listerConfirmBusy || listerIsWaiting}
              className="flex min-w-[8rem] flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-brand-primary py-2 text-sm font-semibold text-white active:opacity-80 disabled:opacity-50"
            >
              {listerConfirmBusy ? (
                <IonSpinner name="crescent" className="h-4 w-4" />
              ) : ordererHasConfirmed ? (
                t('card.confirm.complete')
              ) : listerIsWaiting ? (
                t('card.confirm.waitingCta')
              ) : (
                t('card.confirm.handoffCta')
              )}
            </button>
          </div>
        </div>
      )}

      {/* Post-cancel / no-show payment summary — orderer view only. The lister
          side has a fuller financial dashboard elsewhere. Expired always means
          full void (no fee path), so it short-circuits to the no-charge copy. */}
      {!isLister && (status === 'canceled' || status === 'expired') && (
        <div className="rounded-lg bg-muted/30 p-3 text-sm">
          {(order.amountCaptured ?? 0) > 0 ? (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('card.cancellationFee')}</span>
              <span className="font-semibold text-brand-danger">
                {formatPriceCents(order.amountCaptured)}
              </span>
            </div>
          ) : (
            <p className="text-muted-foreground">
              {t(
                status === 'expired'
                  ? 'card.expiredNoCharge'
                  : !isLister && order.canceledBy === 'lister'
                    ? 'card.canceledByChefNoCharge'
                    : 'card.canceledNoCharge',
              )}
            </p>
          )}
        </div>
      )}

      {!isLister && status === 'no_show' && (
        <div className="rounded-lg bg-brand-danger/10 p-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-brand-danger font-medium">{t('card.noShowBadge')}</span>
            <span className="font-semibold">
              {formatPriceCents(order.amountCaptured)}
            </span>
          </div>
        </div>
      )}

      {/* Action buttons — lister-only (Accept / Ready for Pickup / Reject / No-show / Reopen) */}
      {isLister && (nextAction || canCancel || canMarkNoShow || canReopenNoShow) && (
        // flex-wrap + equal-width buttons (flex-1 with a rem min-width): they
        // split the row evenly at normal text and stack to full-width rows at
        // large text instead of crunching. whitespace-nowrap keeps each label
        // on one line.
        <div className="flex flex-wrap gap-2">
          {canReopenNoShow && (
            <button
              type="button"
              onClick={handleReopenNoShow}
              disabled={isBusy}
              className="flex min-w-[8rem] flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-brand-primary py-2 text-sm font-semibold text-white active:opacity-80 disabled:opacity-50"
            >
              {isBusy ? (
                <IonSpinner name="crescent" className="h-4 w-4" />
              ) : (
                t(isDropoff ? 'card.action.reopenNoShowDropoff' : 'card.action.reopenNoShow')
              )}
            </button>
          )}
          {nextAction && (
            <button
              type="button"
              onClick={handleAdvance}
              disabled={isBusy}
              className="flex min-w-[8rem] flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-brand-primary py-2 text-sm font-semibold text-white active:opacity-80 disabled:opacity-50"
            >
              {isBusy ? (
                <IonSpinner name="crescent" className="h-4 w-4" />
              ) : (
                t(nextAction.labelKey)
              )}
            </button>
          )}
          {canCancel && (
            <button
              type="button"
              onClick={() => setShowReject(true)}
              disabled={isBusy}
              className="flex min-w-[8rem] flex-1 items-center justify-center whitespace-nowrap rounded-lg btn-outline border-brand-danger px-3 py-2 text-sm font-semibold text-brand-danger active:opacity-80 disabled:opacity-50"
            >
              {LISTER_POST_ACCEPT.includes(status) ? t('card.action.cancel') : t('card.action.reject')}
            </button>
          )}
          {canMarkNoShow && (
            <button
              type="button"
              onClick={() => setShowNoShow(true)}
              disabled={isBusy}
              className="flex min-w-[8rem] flex-1 items-center justify-center whitespace-nowrap rounded-lg bg-brand-danger px-3 py-2 text-sm font-semibold text-white active:opacity-80 disabled:opacity-50"
            >
              {t(isDropoff ? 'card.action.noShowDropoff' : 'card.action.noShow')}
            </button>
          )}
        </div>
      )}

      {/* Review button for completed orders */}
      {showReviewAction && (
        <button
          type="button"
          onClick={() => setShowReview(true)}
          className="w-full rounded-lg bg-brand-primary py-2 text-sm font-semibold text-white active:opacity-80"
        >
          {t('card.leaveReview')}
        </button>
      )}

      <ReviewModal
        isOpen={showReview}
        onClose={() => setShowReview(false)}
        orderId={order.orderId}
        activityId={activityId}
        onReviewed={onReviewed}
      />

      <RejectOrderModal
        isOpen={showReject}
        onClose={() => setShowReject(false)}
        onConfirm={handleReject}
        isPending={statusMutation.isPending}
        mode={LISTER_POST_ACCEPT.includes(status) ? 'lister-cancel' : 'reject'}
        fulfillmentMethod={order.fulfillmentMethod}
      />

      <Modal
        isOpen={showNoShow}
        onClose={() => !statusMutation.isPending && setShowNoShow(false)}
        size="sm"
        className="space-y-4"
      >
        <h3 className="text-lg font-bold">{t('card.noShowConfirm.title')}</h3>
        <p className="text-sm text-muted-foreground">
          {/* Trans renders the <strong> wrapping the formatted price. */}
          <Trans
            t={t}
            i18nKey={
              !order.totalPaid
                ? isDropoff ? 'card.noShowConfirm.bodyFreeDropoff' : 'card.noShowConfirm.bodyFree'
                : isDropoff ? 'card.noShowConfirm.bodyDropoff' : 'card.noShowConfirm.body'
            }
            values={{ amount: formatPriceCents(order.totalPaid) }}
            components={{ strong: <span className="font-semibold text-foreground" /> }}
          />
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setShowNoShow(false)}
            disabled={statusMutation.isPending}
            className="flex-1 rounded-lg btn-outline border-border py-2.5 text-sm font-semibold text-foreground active:opacity-80 disabled:opacity-50"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="button"
            onClick={handleNoShow}
            disabled={statusMutation.isPending}
            className="flex-1 rounded-lg bg-brand-danger py-2.5 text-sm font-semibold text-white active:opacity-80 disabled:opacity-50"
          >
            {statusMutation.isPending ? (
              <IonSpinner name="crescent" className="h-4 w-4" />
            ) : (
              t('common:actions.confirm')
            )}
          </button>
        </div>
      </Modal>
    </div>
  );
};
